import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PokerGameModel, PokerPlayerActionType, WSChatMessage } from 'mvppoker-portal-common/api'
import stateUpdateController from 'mvppoker-portal-common/logic/GameStateUpdateController'

export interface ActiveGame {
	game?: PokerGameModel
	gameChat: WSChatMessage[]
	hasUnreadChat: boolean
	isQuitting: boolean
	showSNGFinalStandingsPopup: boolean
	globalVoiceVolume?: number
	globalSFXVolume?: number
	isMicrophoneMuted: boolean
	isJoiningWaitingList: boolean
}

const initialState: ActiveGame = {
	game: undefined,
	gameChat: [],
	hasUnreadChat: false,
	isQuitting: false,
	showSNGFinalStandingsPopup: false,
	isMicrophoneMuted: true,
	isJoiningWaitingList: false,
}

export const activeGameSlice = createSlice({
	name: 'ActiveGame',
	initialState,
	reducers: {
		setJoiningWaitingList: (state: ActiveGame, action: PayloadAction<boolean>) => {
			state.isJoiningWaitingList = action.payload
		},
		setMicrophoneMuted: (state: ActiveGame, action: PayloadAction<boolean>) => {
			state.isMicrophoneMuted = action.payload
		},
		toggleMicrophoneMuted: (state: ActiveGame) => {
			state.isMicrophoneMuted = !state.isMicrophoneMuted
		},
		setGlobalVoiceVolume: (state: ActiveGame, action: PayloadAction<number>) => {
			state.globalVoiceVolume = action.payload
		},
		setGlobalSFXVolume: (state: ActiveGame, action: PayloadAction<number>) => {
			state.globalSFXVolume = action.payload
		},
		updateActiveGame: (state: ActiveGame, action: PayloadAction<PokerGameModel>) => {
			state.game = action.payload
		},
		makeActiveGame: (state: ActiveGame, action: PayloadAction<PokerGameModel>) => {
			state.game = stateUpdateController.prepareState(action.payload)
			state.gameChat = []
		},
		clearActiveGame: (state: ActiveGame) => {
			state.game = undefined
			state.gameChat = []
			state.hasUnreadChat = false
			state.showSNGFinalStandingsPopup = false
		},
		updateGameChat: (state: ActiveGame, action: PayloadAction<WSChatMessage[]>) => {
			state.gameChat = action.payload
		},
		addGameChatMessage: (state: ActiveGame, action: PayloadAction<WSChatMessage>) => {
			const chat = [...state.gameChat]
			chat.push(action.payload)
			state.gameChat = chat
			state.hasUnreadChat = true
		},
		setGameChatRead: (state: ActiveGame) => {
			state.hasUnreadChat = false
		},
		updateUserWantedAction: (state: ActiveGame, action: PayloadAction<{ userId: string; actionType?: PokerPlayerActionType }>) => {
			const { userId, actionType } = action.payload
			const user = state.game?.players.find((it) => it.profile.userId === userId)
			if (user) {
				user.wantedAction = actionType
			}
		},
		setUserQuitting: (state: ActiveGame, action: PayloadAction<boolean>) => {
			state.isQuitting = action.payload
		},
		showSNGFinalStandingsPopup: (state: ActiveGame, action: PayloadAction<boolean>) => {
			state.showSNGFinalStandingsPopup = action.payload
		},
		updateWantedAction: (state: ActiveGame, action: PayloadAction<{ action?: PokerPlayerActionType; userId: string }>) => {
			const player = state.game?.players?.find((it) => it.profile.userId === action.payload.userId)
			if (player) {
				player.wantedAction = action.payload.action
			}
		}
	},
})

export const {
	setMicrophoneMuted,
	toggleMicrophoneMuted,
	setGlobalVoiceVolume,
	setGlobalSFXVolume,
	makeActiveGame,
	clearActiveGame,
	updateActiveGame,
	updateUserWantedAction,
	setUserQuitting,
	showSNGFinalStandingsPopup,
	updateWantedAction,
	setJoiningWaitingList,
	updateGameChat,
	addGameChatMessage,
	setGameChatRead
} = activeGameSlice.actions

export default activeGameSlice.reducer
